import React, { useState, useEffect } from 'react';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';

const AdminDashboard = () => {
  const [properties, setProperties] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [operationsUsers, setOperationsUsers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [inspectionForm, setInspectionForm] = useState({
    scheduledDate: '',
    assignedTo: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [propertiesRes, inspectionsRes, usersRes, analyticsRes] = await Promise.all([
        api.get('/api/properties'),
        api.get('/api/inspections'),
        api.get('/api/users'),
        api.get('/api/analytics/admin'),
      ]);
      setProperties(propertiesRes.data);
      setInspections(inspectionsRes.data);
      setOperationsUsers(usersRes.data.filter(u => u.role === 'operations'));
      setAnalytics(analyticsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };

  const handleScheduleInspection = async (propertyId) => {
    if (!inspectionForm.scheduledDate || !inspectionForm.assignedTo) {
      alert('Please fill all fields');
      return;
    }
    try {
      await api.post('/api/inspections', {
      propertyId,
      ...inspectionForm,
    });
      setSelectedProperty(null);
      setInspectionForm({ scheduledDate: '', assignedTo: '' });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to schedule inspection');
    }
  };

  const handleReviewInspection = async (inspectionId, decision) => {
    try {
      await api.post(`/api/inspections/${inspectionId}/review`, { decision });
      fetchData();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to review inspection');
    }
  };

  const pendingProperties = properties.filter(p => p.status === 'pending');
  const completedInspections = inspections.filter(i => i.status === 'completed');

  return (
    <DashboardLayout title="Admin Dashboard">
      {analytics && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Properties</h3>
            <div className="value">{analytics.totalProperties}</div>
          </div>
          <div className="stat-card">
            <h3>Pending Properties</h3>
            <div className="value">{pendingProperties.length}</div>
          </div>
          <div className="stat-card">
            <h3>Occupancy Rate</h3>
            <div className="value">{analytics.occupancyRate?.toFixed(1) || 0}%</div>
          </div>
          <div className="stat-card">
            <h3>Total Revenue</h3>
            <div className="value">${analytics.totalRevenue?.toLocaleString() || 0}</div>
          </div>
        </div>
      )}

      <div className="card">
        <h2>Pending Property Submissions</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Title</th>
              <th>Address</th>
              <th>Owner</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingProperties.map((property) => (
              <tr key={property.id}>
                <td>{property.title}</td>
                <td>{property.address}</td>
                <td>{property.ownerId}</td>
                <td>
                  <span className="badge badge-warning">{property.status}</span>
                </td>
                <td>
                  <button
                    onClick={() => setSelectedProperty(property)}
                    className="btn btn-primary btn-sm"
                  >
                    Schedule Inspection
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedProperty && (
        <div className="card">
          <h3>Schedule Inspection for: {selectedProperty.title}</h3>
          <form onSubmit={(e) => {
            e.preventDefault();
            handleScheduleInspection(selectedProperty.id);
          }}>
            <div className="form-group">
              <label>Scheduled Date</label>
              <input
                type="datetime-local"
                value={inspectionForm.scheduledDate}
                onChange={(e) => setInspectionForm({ ...inspectionForm, scheduledDate: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Assign To</label>
              <select
                value={inspectionForm.assignedTo}
                onChange={(e) => setInspectionForm({ ...inspectionForm, assignedTo: e.target.value })}
                required
              >
                <option value="">Select Operations Staff</option>
                {operationsUsers.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.name}
                  </option>
                ))}
              </select>
            </div>
            <button type="submit" className="btn btn-success">Schedule</button>
            <button
              type="button"
              onClick={() => setSelectedProperty(null)}
              className="btn btn-secondary"
              style={{ marginLeft: '10px' }}
            >
              Cancel
            </button>
          </form>
        </div>
      )}

      <div className="card">
        <h2>Completed Inspections (Pending Review)</h2>
        <table className="table">
          <thead>
            <tr>
              <th>Property</th>
              <th>Assigned To</th>
              <th>Recommendation</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {completedInspections.map((inspection) => {
              const property = properties.find(p => p.id === inspection.propertyId);
              return (
                <tr key={inspection.id}>
                <td>{property?.title || 'N/A'}</td>
                <td>{inspection.assignedTo}</td>
                <td>
                  <span className={`badge badge-${inspection.recommendation === 'approve' ? 'success' : 'danger'}`}>
                    {inspection.recommendation}
                  </span>
                </td>
                <td>
                  <button
                    onClick={() => handleReviewInspection(inspection.id, 'approve')}
                    className="btn btn-success btn-sm"
                    style={{ marginRight: '5px' }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => handleReviewInspection(inspection.id, 'reject')}
                    className="btn btn-danger btn-sm"
                  >
                    Reject
                  </button>
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>
    </DashboardLayout>
  );
};

export default AdminDashboard;

